import { NodeSingular } from "cytoscape";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { addNodes, Entity, GraphNode, updateNodes } from "../../../api";
import { zGraphNode } from "../../../api/zod.gen.ts";
import { getNodeDataFromCytoscape } from "../../../services/graph/get-data-from-graph.ts";
import { $syncedGraphContent } from "../../../stores/graph/content.ts";
import { $cytoscapeInstance } from "../../../stores/graph/ui.ts";
import { updateNodePosition } from "../update-graph.ts";
import { createGraphNodeModel } from "./create-graph-node-model.ts";
import { handleOperationError } from "./handle-operation-error.ts";

/**
 * Rounds position coordinates from floating point to integers
 * Ensures strict integer values for backend compatibility
 */
const roundPosition = (
  position: GraphNode["position"],
): GraphNode["position"] => ({
  x: Math.round(position?.x ?? 0),
  y: Math.round(position?.y ?? 0),
});

const createPosition = (
  x: number,
  y: number,
  offset = 20,
): GraphNode["position"] =>
  roundPosition({
    x: x + offset,
    y: y + offset,
  });

export const nodeOperations = {
  async addManyToGraph(
    graphId: string,
    list: { entity: Entity; value: string }[],
  ): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content) return;

    const mappedNodes = list.map(({ entity, value }, idx) => {
      const position = createPosition(100 * idx, 100 * idx);
      return createGraphNodeModel({
        entity,
        position,
        value,
      });
    });

    $syncedGraphContent.set({
      ...content,
      nodes: [...content.nodes, ...mappedNodes],
    });

    await addNodes({ body: mappedNodes, path: { id: graphId } });
  },

  async addToGraph(graphId: string, node: GraphNode): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content) return;

    const nodeWithRoundedPosition: GraphNode = {
      ...node,
      position: roundPosition(node.position),
    };

    $syncedGraphContent.set({
      ...content,
      nodes: [...content.nodes, nodeWithRoundedPosition],
    });

    await addNodes({ body: [nodeWithRoundedPosition], path: { id: graphId } });
  },

  async copy(): Promise<void> {
    const selectedElements = $cytoscapeInstance.get()?.$("node:selected");

    if (!selectedElements?.length) return;

    try {
      const mappedNodes: GraphNode[] = selectedElements.map(
        (node: NodeSingular) => ({
          colorSchema: getNodeDataFromCytoscape(node, "colorSchema"),
          entityId: getNodeDataFromCytoscape(node, "entityId"),
          entityName: getNodeDataFromCytoscape(node, "entityName"),
          entityQualifiedName: getNodeDataFromCytoscape(
            node,
            "entityQualifiedName",
          ),
          icon: getNodeDataFromCytoscape(node, "icon"),
          id: node.id(),
          position: roundPosition(node.position()),
          value: getNodeDataFromCytoscape(node, "value"),
        }),
      );

      await navigator.clipboard.writeText(JSON.stringify(mappedNodes));
      toast.info("Nodes copied to clipboard");
    } catch (error) {
      handleOperationError("copy nodes", error);
    }
  },

  async duplicate({
    graphId,
    nodeId,
  }: {
    graphId: string;
    nodeId: string;
  }): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content) return;

    const nodeToDuplicate = content.nodes.find((node) => node.id === nodeId);
    if (!nodeToDuplicate) return;

    const duplicatedNode: GraphNode = {
      ...nodeToDuplicate,
      id: uuidv4(), // New unique ID
      position: {
        x: (nodeToDuplicate.position?.x ?? 0) + 50,
        y: (nodeToDuplicate.position?.y ?? 0) + 50,
      },
    };

    $syncedGraphContent.set({
      ...content,
      nodes: [...content.nodes, duplicatedNode],
    });

    await addNodes({ body: [duplicatedNode], path: { id: graphId } });
  },

  handleNodePositionUpdate(
    event: cytoscape.EventObject,
    graphId: null | string,
  ): void {
    if (!graphId) return;

    const node = event.target;
    const roundedPosition = roundPosition(node.position());
    updateNodePosition(graphId, node.id(), roundedPosition);
  },

  async paste(graphId: string): Promise<void> {
    const clipboardContent = await navigator.clipboard.readText();
    const parsedData = JSON.parse(clipboardContent);
    const validationResult = z.array(zGraphNode).safeParse(parsedData);

    if (!validationResult.success) {
      toast.error("Invalid node data in clipboard.");
      console.error("Validation errors:", validationResult.error);
      return;
    }

    const copiedNodes: GraphNode[] = validationResult.data;
    const content = $syncedGraphContent.get();
    if (!content) return;

    const mappedNodes = copiedNodes.map((node) => ({
      ...node,
      id: uuidv4(),
      position: roundPosition({
        x: node.position?.x ?? 0 + 20,
        y: node.position?.y ?? 0 + 20,
      }),
    }));

    $syncedGraphContent.set({
      ...content,
      nodes: [...content.nodes, ...mappedNodes],
    });

    await addNodes({ body: mappedNodes, path: { id: graphId } });

    toast.success("Nodes pasted");
  },

  selectNeighbors(nodeId: string): void {
    const cy = $cytoscapeInstance.get();
    if (!cy) return;

    cy.elements().unselect();

    cy.nodes()
      .filter(`[id = "${nodeId}"]`)
      .connectedEdges()
      .connectedNodes()
      .not(`[id = "${nodeId}"]`)
      .select();

    cy.one("render", () => {
      cy.fit(cy.nodes(":selected"), 50);
    });
  },

  async updateNodeFlag(
    graphId: string,
    nodeId: string,
    flagColor: string,
    isFlagged: boolean,
  ): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content) return;

    const nodeIndex = content.nodes.findIndex((node) => node.id === nodeId);
    if (nodeIndex === -1) return;

    const updatedNode = {
      ...content.nodes[nodeIndex],
      flagColor: isFlagged ? flagColor : undefined,
      isFlagged: isFlagged,
    };

    const updatedNodes = [...content.nodes];
    updatedNodes[nodeIndex] = updatedNode;

    $syncedGraphContent.set({
      ...content,
      nodes: updatedNodes,
    });

    await updateNodes({ body: [updatedNode], path: { id: graphId } });
  },

  async updateNodeFlags(
    graphId: string,
    nodeIds: string[],
    flagColor: string,
    isFlagged: boolean,
  ): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content || nodeIds.length === 0) return;

    // Helper function to update a single node's flag
    const updateNodeFlag = (node: GraphNode): GraphNode => {
      if (!nodeIds.includes(node.id)) return node;

      return {
        ...node,
        flagColor: isFlagged ? flagColor : undefined,
        isFlagged: isFlagged,
      };
    };

    const updatedNodes = content.nodes.map(updateNodeFlag);

    $syncedGraphContent.set({
      ...content,
      nodes: updatedNodes,
    });

    const nodesToUpdate = nodeIds.map((id) => ({ flagColor, id, isFlagged }));

    await updateNodes({
      body: nodesToUpdate,
      path: { id: graphId },
    });
  },

  updateNodeMeta<K extends keyof Pick<GraphNode, "position" | "value">>(
    graphId: string,
    nodeId: string,
    key: K,
    newValue: Pick<GraphNode, "position" | "value">[K],
  ): void {
    const content = $syncedGraphContent.get();
    if (!content) return;

    const nodeIndex = content.nodes.findIndex((node) => node.id === nodeId);
    if (nodeIndex === -1) return;

    const processedValue =
      key === "position"
        ? roundPosition(newValue as GraphNode["position"])
        : newValue;

    const updatedNode = {
      ...content.nodes[nodeIndex],
      [key]: processedValue,
    };

    const updatedNodes = [...content.nodes];
    updatedNodes[nodeIndex] = updatedNode;

    $syncedGraphContent.set({
      ...content,
      nodes: updatedNodes,
    });

    void updateNodes({ body: [updatedNode], path: { id: graphId } });
  },
};
